import axios from 'axios';
import { auth } from './firebase';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const API_KEY = process.env.REACT_APP_API_SECRET_KEY;

// Helper to get auth headers
const getHeaders = async () => {
  const headers = {
    'Content-Type': 'application/json',
  };

  // Add API key if available
  if (API_KEY) {
    headers['x-api-key'] = API_KEY;
  }

  return headers;
};

// Helper to extract JSON from text
const extractJSON = (text) => {
  try {
    const jsonMatch = text.match(/\{[\s\S]*\}/);
    return jsonMatch ? JSON.parse(jsonMatch[0]) : {};
  } catch (e) {
    console.error("JSON extraction error:", e);
    return {};
  }
};

// Helper to get user ID
const getUserId = () => {
  return auth.currentUser?.uid || 'anonymous';
};

export const generateExplanation = async (topic) => {
  try {
    const headers = await getHeaders();
    const userId = getUserId();

    const prompt = `You are a world-class educator with expertise in breaking down complex concepts. Explain "${topic}" in THREE comprehensive styles.

Instructions:

1. SIMPLE EXPLANATION (8-10 sentences):
   - Start with what ${topic} actually is in plain English
   - Explain WHY it matters and where it's used
   - Break down core concepts step-by-step
   - Use everyday language, avoid jargon
   - Include 1-2 simple examples
   - End with key takeaway

2. DETAILED EXPLANATION (15-20 sentences):
   - Provide comprehensive technical overview
   - Explain underlying principles and mechanisms
   - Discuss historical context or development
   - Cover main applications and use cases
   - Include specific examples with details
   - Explain how different parts interact
   - Mention related concepts and connections
   - Address common misconceptions
   - Discuss advantages and limitations
   - Conclude with advanced applications

3. ANALOGY EXPLANATION (12-15 sentences):
   - Create an elaborate real-world analogy
   - Map each major concept to analogy elements
   - Walk through the analogy step-by-step
   - Show how the analogy parallels the actual concept
   - Extend the analogy to cover edge cases
   - Explain where the analogy breaks down
   - Reinforce key concepts through comparison
   - Make it memorable and engaging

Return ONLY valid JSON:
{
  "simple": "Your simple explanation (8-10 sentences)",
  "detailed": "Your detailed CBSE/ICSE style explanation (15-20 sentences)", 
  "analogy": "Your Indian context analogy explanation (12-15 sentences)"
}`;

    const response = await axios.post(
      `${API_URL}/chat`,
      {
        userId,
        message: prompt,
      },
      { headers }
    );

    const aiResponse = response.data.reply;
    const data = extractJSON(aiResponse);

    if (!data.simple || !data.detailed || !data.analogy) {
      throw new Error('Invalid response format');
    }

    return data;
  } catch (error) {
    console.error('Explanation error:', error);
    return {
      simple: `${topic} is a fundamental concept that plays a crucial role in understanding how systems and processes work. At its core, ${topic} involves specific principles that govern its behavior and application. It's widely used across multiple domains including science, technology, and everyday problem-solving. To understand ${topic}, you need to grasp its basic components and how they interact with each other. Think of it as a building block that supports more complex ideas. When you learn about ${topic}, you're essentially learning a language that helps you communicate and solve problems more effectively. It has practical applications that you encounter regularly, even if you don't realize it. The key to mastering ${topic} is understanding not just what it is, but why it works the way it does and how to apply it in different contexts.`,
      
      detailed: `${topic} is a comprehensive and multifaceted concept that has evolved significantly over time and continues to be central to many fields of study and practice. Historically, the development of ${topic} emerged from the need to solve specific problems and has since expanded into a robust framework with wide-ranging applications. At its foundation, ${topic} operates on several core principles that define its structure and function. These principles include fundamental rules and patterns that govern how ${topic} behaves under different conditions. Understanding these underlying mechanisms is essential for anyone looking to apply ${topic} effectively. The concept encompasses multiple components that work together synergistically, each playing a distinct role in the overall system. In practical applications, ${topic} is utilized in diverse scenarios ranging from theoretical research to hands-on implementation in real-world projects. For example, in technical contexts, ${topic} serves as the backbone for designing systems, optimizing processes, and solving complex challenges. One of the remarkable aspects of ${topic} is how it connects to related concepts and fields, creating a network of knowledge that enhances understanding across disciplines. However, it's important to note that ${topic} also has limitations and constraints that must be considered when applying it. Common misconceptions about ${topic} often stem from oversimplifying its complexity or misunderstanding its scope. Advanced applications of ${topic} push the boundaries of what's possible, incorporating cutting-edge techniques and innovations.`,
      
      analogy: `Think of ${topic} like running a sophisticated restaurant kitchen during peak dinner service. Just as a restaurant has multiple stations working in harmony—the prep station, the grill, the sauté station, and the plating area—${topic} has different components that must coordinate seamlessly. The head chef acts like the central controller in ${topic}, making decisions, managing resources, and ensuring everything flows smoothly. When an order comes in, it's like inputting data or starting a process in ${topic}—the information needs to be distributed to the right stations at the right time. Each station represents a different aspect or function within ${topic}, specializing in specific tasks but contributing to the final outcome. The prep cooks who chop vegetables and prepare ingredients are like the foundational elements of ${topic} that must be ready before the main processes begin. As the cooking begins, timing becomes critical—just like in ${topic}, where sequence and synchronization matter tremendously. If the grill station finishes the steak before the sauté station completes the vegetables, the food gets cold, similar to how poor coordination in ${topic} leads to inefficiencies or errors. The expeditor who calls out orders and checks quality control is analogous to validation and error-checking mechanisms in ${topic}. When something goes wrong—a dish is overseasoned or undercooked—the kitchen must adapt and correct course, just as ${topic} systems need error handling and recovery processes. The restaurant's reputation depends on consistency and quality, which parallels how ${topic} must deliver reliable results.`,
    };
  }
};

export const generateQuiz = async (topic) => {
  try {
    const headers = await getHeaders();
    const userId = getUserId();

    const prompt = `Create 5 CBSE/ICSE board style multiple-choice questions about "${topic}" following Indian board examination pattern.

Requirements:
- Question 1-2: Easy (1-2 marks level - basic recall and understanding)
- Question 3-4: Medium (3-4 marks level - application and analysis with Indian context)
- Question 5: Hard (5-6 marks level - synthesis and evaluation)
- Use Indian examples (Indian cities, currency, measurements, scientists)
- Follow NCERT/board textbook question style
- Include numerical problems with Indian units (km, kg, rupees)
- Options should be similar to board exam multiple choice
- Avoid trick questions, focus on conceptual understanding
- Reference Class 9-12 syllabus level

Return ONLY valid JSON:
{
  "questions": [
    {
      "question": "CBSE/ICSE style question with Indian context?",
      "options": ["option1", "option2", "option3", "option4"],
      "correctAnswer": "option1"
    }
  ]
}`;

    const response = await axios.post(
      `${API_URL}/chat`,
      {
        userId,
        message: prompt,
      },
      { headers }
    );

    const aiResponse = response.data.reply;
    const data = extractJSON(aiResponse);

    if (!data.questions || data.questions.length === 0) {
      throw new Error('Invalid response');
    }

    return data;
  } catch (error) {
    console.error('Quiz error:', error);
    return {
      questions: [
        {
          question: `According to CBSE syllabus, what is the primary application of ${topic} in Indian context?`,
          options: ['Solving real-world problems in Indian industries', 'Only for theoretical board exam questions', 'Memorizing formulas without understanding', 'Limited to foreign examples only'],
          correctAnswer: 'Solving real-world problems in Indian industries',
        },
        {
          question: `Which statement best describes ${topic} as per NCERT guidelines?`,
          options: ['A fundamental concept with practical applications in India', 'An outdated Western theory not relevant to India', 'Only for students planning to study abroad', 'Too advanced for Indian board curriculum'],
          correctAnswer: 'A fundamental concept with practical applications in India',
        },
        {
          question: `In the context of Indian board examinations, ${topic} is most commonly tested through:`,
          options: ['Numerical problems with step-by-step solutions', 'Only multiple choice questions', 'Memorization of definitions only', 'Essay-type subjective answers only'],
          correctAnswer: 'Numerical problems with step-by-step solutions',
        },
        {
          question: `For scoring maximum marks in ${topic} questions in CBSE/ICSE boards, students should:`,
          options: ['Focus on understanding concepts and practicing numerical problems', 'Only memorize formulas without understanding', 'Skip this topic as it\'s too difficult', 'Study only one day before exam'],
          correctAnswer: 'Focus on understanding concepts and practicing numerical problems',
        },
        {
          question: `${topic} connects to other subjects in Indian board curriculum primarily through:`,
          options: ['Interdisciplinary applications in Mathematics, Physics, and Chemistry', 'Only Mathematics subject', 'No connection with other subjects', 'Only theoretical connections without practical use'],
          correctAnswer: 'Interdisciplinary applications in Mathematics, Physics, and Chemistry',
        },
      ],
    };
  }
};

export const generateFeedback = async (topic, score, answers) => {
  try {
    const headers = await getHeaders();
    const userId = getUserId();

    const prompt = `Provide comprehensive, personalized feedback for an Indian CBSE/ICSE board student who scored ${score}% on a "${topic}" quiz.

Include:
1. Warm acknowledgment in Indian teaching style (2 sentences)
2. Performance analysis in board exam context (2-3 sentences)
3. Specific study recommendations following Indian board pattern (4-5 sentences)
4. Subject-wise preparation tips for CBSE/ICSE (2-3 sentences)
5. Motivational conclusion with Indian cultural references (2 sentences)

Reference:
- NCERT textbooks and board exam patterns
- Indian educational context and examples
- Scoring strategies for board examinations
- Use some Hindi words where appropriate (beta, padhai, etc.)

Total: 12-15 sentences. Be supportive like an Indian teacher.`;

    const response = await axios.post(
      `${API_URL}/chat`,
      {
        userId,
        message: prompt,
      },
      { headers }
    );

    return response.data.reply;
  } catch (error) {
    console.error('Feedback error:', error);
    if (score >= 80) {
      return `Bahut accha beta! आपका ${score}% score show करता है कि आपने ${topic} को properly समझा है और board exam के लिए well-prepared हैं। Your performance indicates strong conceptual understanding which is exactly what CBSE/ICSE examiners look for in answer sheets. Keep up this excellent work और जैसे Sachin Tendulkar ने consistent practice से success achieve की, आप भी consistent effort से academic excellence achieve करेंगे!`;
    } else if (score >= 60) {
      return `अच्छी कोशिश की है beta! ${score}% score decent है लेकिन board exams में अच्छे marks के लिए आपको और भी practice करनी होगी। NCERT textbook को thoroughly पढ़िए और हर chapter के end में दिए गए questions को solve करिए। With focused review and consistent practice, you'll see significant improvement. Don't give up!`;
    } else {
      return `कोई बात नहीं beta, ${score}% से डरने की बात नहीं है - यह सिर्फ starting point है! Sabse पहले आपको ${topic} के basic concepts को solid करना होगा। Daily study schedule बनाइए और small targets set करिए। Remember करिए कि Dr. Kalam भी average student थे initially, लेकिन उन्होंने never give up नहीं किया। Keep trying, keep learning!`;
    }
  }
};

export const simplifyText = async (text) => {
  try {
    const headers = await getHeaders();
    const userId = getUserId();

    const prompt = `Simplify this text for Indian CBSE/ICSE board students to understand easily in Indian educational context.

Requirements:
- Use simple Hindi-English mix (Hinglish) where appropriate
- Break into short, clear sentences
- Add examples from Indian context (cities, festivals, daily life)
- Explain technical terms using familiar Indian examples
- Make it friendly like an Indian teacher explaining
- Reference NCERT textbook style explanations
- Use measurements and examples familiar to Indian students

Original text:
"${text}"

Provide a clear, beginner-friendly simplified version for Indian students (6-8 sentences).`;

    const response = await axios.post(
      `${API_URL}/chat`,
      {
        userId,
        message: prompt,
      },
      { headers }
    );

    return response.data.reply;
  } catch (error) {
    console.error('Simplify error:', error);
    return text;
  }
};

// Clear chat history for the user
export const clearChatHistory = async () => {
  try {
    const headers = await getHeaders();
    const userId = getUserId();

    await axios.delete(`${API_URL}/chat`, {
      headers,
      data: { userId }
    });

    console.log('Chat history cleared successfully');
  } catch (error) {
    console.error('Error clearing chat history:', error);
  }
};